import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { useCreatorMe } from '../../lib/auth';
import { SplashLoader } from '../../components/SplashLoader';
import { queryClient } from '../../lib/queryClient';
import logoWhite from '../../assets/logo-white.svg';
import styles from './CreatorLoginPage.module.css';

// WelcomePage is the chrome-less landing for first-time creators who clicked
// the approval email link. Backend redirects here via /auth/creator/callback
// whenever an account has no password set. Returning users (has_password=true)
// who land here directly are bounced to /creator.
export function WelcomePage() {
  const me = useCreatorMe();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  if (me.isPending) return <SplashLoader />;
  // Auth-gate is handled by CreatorBareLayout; if we got here me.data exists.
  if (me.data?.has_password) return <Navigate to="/creator" replace />;

  const firstName = (me.data?.submission?.fullName || me.data?.email || '').split(' ')[0] || '';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    if (password.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/creator/set-password', { currentPassword: '', newPassword: password });
      await queryClient.invalidateQueries({ queryKey: ['creator', 'me'] });
      navigate('/creator', { replace: true });
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Could not set password. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.shell}>
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <img src={logoWhite} alt="Kick" className={styles.logo} />
          <h1 className={styles.heroTitle}>
            {firstName ? `Welcome, ${firstName}` : 'Welcome to Kick'}
          </h1>
          <p className={styles.heroSub}>Set a password to finish creating your account</p>
        </div>
      </div>

      <div className={styles.cardWrap}>
        <div className={styles.card}>
          <div className={styles.formPad}>
            <form onSubmit={submit}>
              {err && <div className={styles.errorBanner}>{err}</div>}
              <label className={styles.label} htmlFor="welcome-password">
                New password
              </label>
              <input
                id="welcome-password"
                type="password"
                placeholder="At least 8 characters"
                autoComplete="new-password"
                className={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <label className={styles.label} htmlFor="welcome-confirm">
                Confirm password
              </label>
              <input
                id="welcome-confirm"
                type="password"
                placeholder="Re-enter your password"
                autoComplete="new-password"
                className={styles.input}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              <button className={styles.btn} disabled={submitting} type="submit">
                {submitting ? 'Setting password…' : 'Set password and continue'}
              </button>
            </form>
          </div>
        </div>
      </div>
      <footer className={styles.footer}>© 2026 Kick — All rights reserved</footer>
    </div>
  );
}
